// fallow-ignore-file code-duplication
import React, { useState, useMemo, useEffect, useRef } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTable } from '@/components/reports/ReportTable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  useDailyEarningsPYE,
  useRefreshPYESnapshot,
} from '@/api/rebates/useReports';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import {
  ShareNetworkIcon,
  LinkIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { downloadRebateMultiTableXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import { RebateReportDownloadMenu } from '@/components/rebates/reports/export/RebateReportDownloadMenu';
import {
  rebateMultiTableDownloadFilename,
  rebateMultiTableFinalFilename,
} from '@/components/rebates/reports/export/rebateMultiTableDownloadFilename';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import { sortReportTableByDivision } from '@/utils/reportTableUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { T, useGT } from 'gt-react';
import { HeaderPageTitle } from 'src/context/header-title.context';

const REPORT_ID = 'daily-earnings-pye';

export interface DailyEarningsPYEPageProps {
  /** When provided, these filters are locked and their UI is hidden */
  lockedFilters?: {
    year: number;
    divisionIds?: string[];
    categoryFilters?: string[];
  };
}

export function DailyEarningsPYEPage({
  lockedFilters,
}: DailyEarningsPYEPageProps) {
  const gt = useGT();
  const REPORT_TITLE = gt('Daily Earnings PYE', {
    context: 'report title for Daily Earnings Post Year-End report',
  });
  const isShared = !!lockedFilters;

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const defaultYear = lockedFilters?.year ?? currentYear - 1; // Previous completed year
  const [selectedYear, setSelectedYear] = useState<number>(defaultYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    MultiSelectOption[]
  >([]);
  const hasSetDefaultDivision = useRef(false);
  const hasSetDefaultCategories = useRef(false);
  const [priorYearLineType, setPriorYearLineType] = useState<
    'reported' | 'estimates' | 'predictions'
  >('estimates');
  const [throughMonth, setThroughMonth] = useState<number>(currentMonth);

  const monthNames = useMemo(
    () => [
      gt('January', { context: 'calendar month name' }),
      gt('February', { context: 'calendar month name' }),
      gt('March', { context: 'calendar month name' }),
      gt('April', { context: 'calendar month name' }),
      gt('May', { context: 'calendar month name' }),
      gt('June', { context: 'calendar month name' }),
      gt('July', { context: 'calendar month name' }),
      gt('August', { context: 'calendar month name' }),
      gt('September', { context: 'calendar month name' }),
      gt('October', { context: 'calendar month name' }),
      gt('November', { context: 'calendar month name' }),
      gt('December', { context: 'calendar month name' }),
    ],
    [gt],
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'xlsx'>('csv');

  const refreshSnapshotMutation = useRefreshPYESnapshot();

  // Fetch divisions for filter (skip if locked)
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(!isShared);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Prepare category options
  const categoryOptions: MultiSelectOption[] = useMemo(
    () => [
      {
        id: 'Redistribution',
        label: gt('Redistribution', {
          context: 'rebate category option label',
        }),
        value: 'Redistribution',
      },
      {
        id: 'Conditional',
        label: gt('Conditional', { context: 'rebate category option label' }),
        value: 'Conditional',
      },
      {
        id: 'Non-Conditional',
        label: gt('Non-Conditional', {
          context: 'rebate category option label',
        }),
        value: 'Non-Conditional',
      },
    ],
    [gt],
  );

  // Prepare prior year line type options for comparative table
  const priorYearLineTypeOptions = useMemo(
    () => [
      {
        id: 'estimates',
        label: gt('Estimates', {
          context: 'prior year data type option label',
        }),
        value: 'estimates' as const,
      },
      {
        id: 'reported',
        label: gt('Reported', { context: 'prior year data type option label' }),
        value: 'reported' as const,
      },
      {
        id: 'predictions',
        label: gt('Predictions', {
          context: 'prior year data type option label',
        }),
        value: 'predictions' as const,
      },
    ],
    [gt],
  );

  // Determine effective filter values (locked or selected)
  const effectiveYear = lockedFilters?.year ?? selectedYear;
  const effectiveDivisionIds =
    lockedFilters?.divisionIds ??
    (selectedDivisions.length > 0
      ? selectedDivisions.map((d) => d.id)
      : undefined);
  const effectiveCategoryFilters =
    lockedFilters?.categoryFilters ??
    (selectedCategories.length > 0
      ? selectedCategories.map((c) => c.id)
      : undefined);

  // Generate default filename with divisions
  const defaultFilename = useMemo(() => {
    const divisionPart =
      !isShared &&
      selectedDivisions.length > 0 &&
      selectedDivisions.length < divisionOptions.length
        ? `-${selectedDivisions.map((d) => d.label.replace(/\s+/g, '-')).join('-')}`
        : '';
    return `daily-earnings-pye-${effectiveYear}${divisionPart}`;
  }, [effectiveYear, selectedDivisions, divisionOptions.length, isShared]);

  // Handler for downloading all tables
  const handleDownloadClick = (format: 'csv' | 'xlsx') => {
    if (!report) return;
    setDownloadFormat(format);
    setDownloadModalOpen(true);
  };

  const handleDownload = (filename: string) => {
    if (!sortedTable || !sortedMonthlyBreakdownTable) return;

    const options = {
      tables: [
        {
          title:
            sortedTable.title ||
            gt('Category Breakdown', {
              context: 'CSV download table title for Daily Earnings PYE',
            }),
          table: sortedTable,
          rowLabelHeader: gt('Division', {
            context: 'CSV download row label header',
          }),
        },
        {
          title:
            sortedMonthlyBreakdownTable.title ||
            gt('Monthly Breakdown', {
              context: 'CSV download table title for Daily Earnings PYE',
            }),
          table: sortedMonthlyBreakdownTable,
          rowLabelHeader: gt('Division', {
            context: 'CSV download row label header',
          }),
        },
      ],
      filename: rebateMultiTableFinalFilename(filename, downloadFormat),
    };
    if (downloadFormat === 'xlsx') {
      void downloadRebateMultiTableXlsx(options);
      return;
    }
    downloadMultiTableCSV(options);
  };

  // Default to ALL divisions when divisions are loaded (only once, skip if locked)
  useEffect(() => {
    if (isShared) return; // Skip if using locked filters
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([...divisionOptions]);
      hasSetDefaultDivision.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionOptions.length, divisionsLoading, isShared]);

  // Default to ALL categories (only once, skip if locked)
  useEffect(() => {
    if (lockedFilters?.categoryFilters) return; // Skip if categories are locked
    if (
      categoryOptions.length > 0 &&
      selectedCategories.length === 0 &&
      !hasSetDefaultCategories.current
    ) {
      setSelectedCategories([...categoryOptions]);
      hasSetDefaultCategories.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions.length, lockedFilters?.categoryFilters]);

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useDailyEarningsPYE({
    year: effectiveYear,
    divisionIds: effectiveDivisionIds,
    categoryFilters: effectiveCategoryFilters,
    priorYearLineType,
    throughMonth,
  });

  // Sort report tables by custom division order
  const sortedTable = useMemo(() => {
    if (!report?.table) return null;
    return sortReportTableByDivision(report.table);
  }, [report?.table]);

  const sortedMonthlyBreakdownTable = useMemo(() => {
    if (!report?.monthlyBreakdownTable) return null;
    return sortReportTableByDivision(report.monthlyBreakdownTable);
  }, [report?.monthlyBreakdownTable]);

  const pageTitle = (
    <T context="page title for Daily Earnings PYE report">Daily Earnings PYE</T>
  );

  const content = (
    <div
      className={
        isShared ? '' : 'p-1 w-full min-w-0 max-w-full overflow-hidden'
      }
    >
      <div className="mb-2 flex flex-col gap-2">
        {isShared ? (
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
        ) : (
          <HeaderPageTitle>{pageTitle}</HeaderPageTitle>
        )}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {!isShared && (
              <>
                <SingleSelect
                  label={gt('Year', {
                    context: 'year filter label in Daily Earnings PYE report',
                  })}
                  labelPosition="left"
                  options={yearOptions}
                  value={
                    yearOptions.find((opt) => opt.value === selectedYear) ||
                    null
                  }
                  onChange={(option) => {
                    if (option) {
                      setSelectedYear(option.value);
                    }
                  }}
                  placeholder={gt('Select year...', {
                    context:
                      'year filter placeholder in Daily Earnings PYE report',
                  })}
                  searchPlaceholder={gt('Search years...', {
                    context:
                      'year filter search placeholder in Daily Earnings PYE report',
                  })}
                  shouldAutoSort={false}
                />
                <MultiSelect
                  label={gt('Divisions', {
                    context:
                      'divisions filter label in Daily Earnings PYE report',
                  })}
                  labelPosition="left"
                  options={divisionOptions}
                  value={selectedDivisions}
                  onChange={setSelectedDivisions}
                  placeholder={gt('Select divisions...', {
                    context:
                      'divisions filter placeholder in Daily Earnings PYE report',
                  })}
                  searchPlaceholder={gt('Search divisions...', {
                    context:
                      'divisions filter search placeholder in Daily Earnings PYE report',
                  })}
                  disabled={divisionsLoading}
                />
              </>
            )}
            <SingleSelect
              label={gt('Prior Year Data', {
                context:
                  'prior year data filter label in Daily Earnings PYE report',
              })}
              labelPosition="left"
              options={priorYearLineTypeOptions}
              value={
                priorYearLineTypeOptions.find(
                  (opt) => opt.value === priorYearLineType,
                ) || null
              }
              onChange={(option) => {
                if (option) {
                  setPriorYearLineType(option.value);
                }
              }}
              placeholder={gt('Select data type...', {
                context:
                  'prior year data filter placeholder in Daily Earnings PYE report',
              })}
              searchPlaceholder={gt('Search...', {
                context:
                  'prior year data filter search placeholder in Daily Earnings PYE report',
              })}
              shouldAutoSort={false}
            />
            <div className="flex items-center gap-1.5">
              <Label
                htmlFor="through-month"
                className="text-sm text-foreground whitespace-nowrap"
              >
                <T context="label for month-through cutoff selector on rebate report">
                  Through:
                </T>
              </Label>
              <Select
                value={throughMonth.toString()}
                onValueChange={(v) => setThroughMonth(Number(v))}
              >
                <SelectTrigger
                  id="through-month"
                  className="w-[130px] h-8 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((name, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex gap-2">
              <RebateReportDownloadMenu
                disabled={!report || isLoading}
                onDownload={handleDownloadClick}
              />
              {!isShared && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <ShareNetworkIcon className="h-4 w-4 mr-2" />
                      <T context="share button in Daily Earnings PYE report">
                        Share
                      </T>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                      <ShareNetworkIcon className="h-4 w-4 mr-2" />
                      <T context="dropdown item to create share link for Daily Earnings PYE report">
                        Create share link
                      </T>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      <T context="dropdown item to manage shared links for Daily Earnings PYE report">
                        Manage shared links
                      </T>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>
              <T context="loading card title in Daily Earnings PYE report">
                Post Year-End Earnings Report
              </T>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TableSkeleton />
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">
              <T context="error state message in Daily Earnings PYE report">
                Error loading report data. Please try again.
              </T>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Report Tables */}
      {sortedTable && sortedMonthlyBreakdownTable && !isLoading && (
        <div className="space-y-3">
          {/* First Table: Category Breakdown */}
          <ReportTable
            table={sortedTable}
            timeFilter="all"
            onTimeFilterChange={() => {
              // No-op: time filter is hardcoded to "all" (full year)
            }}
            rowLabel={gt('Division', {
              context:
                'row label for Division in Daily Earnings PYE report table',
            })}
            showTotals={false}
            showTimeFilter={false}
          />

          {/* Second Table: Monthly Breakdown */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  <T context="monthly breakdown section title in Daily Earnings PYE report">
                    Monthly Breakdown
                  </T>
                </CardTitle>
                {!isShared && (
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        refreshSnapshotMutation.mutate({
                          fiscalYear: effectiveYear,
                        })
                      }
                      disabled={refreshSnapshotMutation.isPending}
                    >
                      <ArrowsClockwiseIcon
                        className={`h-4 w-4 mr-2 ${refreshSnapshotMutation.isPending ? 'animate-spin' : ''}`}
                      />
                      {refreshSnapshotMutation.isPending
                        ? gt('Refreshing...', {
                            context:
                              'refresh snapshot button loading state in Daily Earnings PYE report',
                          })
                        : gt('Refresh Snapshot', {
                            context:
                              'refresh snapshot button in Daily Earnings PYE report',
                          })}
                    </Button>
                    {report?.lastSnapshotDate && (
                      <p className="text-xs text-muted-foreground">
                        <T context="last refreshed label in Daily Earnings PYE report">
                          Last refreshed:
                        </T>{' '}
                        {new Date(report.lastSnapshotDate).toLocaleString(
                          undefined,
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          },
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            {!lockedFilters?.categoryFilters && (
              <CardContent className="py-3">
                <div className="flex items-center gap-6">
                  <MultiSelect
                    label={gt('Categories', {
                      context:
                        'categories filter label in Daily Earnings PYE monthly breakdown',
                    })}
                    labelPosition="left"
                    options={categoryOptions}
                    value={selectedCategories}
                    onChange={setSelectedCategories}
                    placeholder={gt('Select categories...', {
                      context:
                        'categories filter placeholder in Daily Earnings PYE monthly breakdown',
                    })}
                    searchPlaceholder={gt('Search categories...', {
                      context:
                        'categories filter search placeholder in Daily Earnings PYE monthly breakdown',
                    })}
                    liveUpdate={false}
                  />
                </div>
              </CardContent>
            )}
          </Card>
          <ReportTable
            table={sortedMonthlyBreakdownTable}
            timeFilter="all"
            onTimeFilterChange={() => {
              // No-op: time filter is hardcoded to "all" (full year)
            }}
            rowLabel={gt('Division', {
              context:
                'row label for Division in Daily Earnings PYE monthly breakdown table',
            })}
            showTotals={true}
            showTimeFilter={false}
          />
        </div>
      )}

      {!isShared && (
        <>
          <ShareReportModal
            open={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            reportId={REPORT_ID}
            reportTitle={REPORT_TITLE}
            initialYear={selectedYear}
            initialDivisions={selectedDivisions}
            customFilters={[
              {
                key: 'categoryFilters',
                label: gt('Categories', {
                  context:
                    'categories filter label in Daily Earnings PYE share modal',
                }),
                options: categoryOptions.map((opt) => ({
                  id: opt.id,
                  label: opt.label,
                })),
                initialValue: selectedCategories.map((c) => c.id),
                placeholder: gt('All categories', {
                  context:
                    'categories filter placeholder in Daily Earnings PYE share modal',
                }),
              },
            ]}
          />
          <SharedLinksManager
            open={linksManagerOpen}
            onClose={() => setLinksManagerOpen(false)}
            reportId={REPORT_ID}
            reportTitle={REPORT_TITLE}
            onCreateNew={() => {
              setLinksManagerOpen(false);
              setShareModalOpen(true);
            }}
          />
        </>
      )}
      <DownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownload}
        defaultFilename={rebateMultiTableDownloadFilename(
          defaultFilename,
          downloadFormat,
        )}
        title={
          downloadFormat === 'xlsx'
            ? gt('Download Daily Earnings PYE (.xlsx)', {
                context:
                  'download modal title for Daily Earnings PYE report as Excel',
              })
            : gt('Download Daily Earnings PYE (.csv)', {
                context:
                  'download modal title for Daily Earnings PYE report as CSV',
              })
        }
        confirmLabel={
          downloadFormat === 'xlsx'
            ? gt('Download Excel', {
                context: 'download Excel button in Daily Earnings PYE report',
              })
            : gt('Download CSV', {
                context: 'download CSV button in Daily Earnings PYE report',
              })
        }
      />
    </div>
  );

  // Wrap in DashboardLayout only when not shared
  if (isShared) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
}

// Default export for routing
const DailyEarningsPYEPageDefault: React.FC = () => {
  return <DailyEarningsPYEPage />;
};

export default DailyEarningsPYEPageDefault;
